import React, { useState, useEffect, useContext } from 'react';
import { useTranslation } from 'react-i18next';
import axios from 'axios';
import { AppContext } from '../../App';
import AdminLayout from '../../components/AdminLayout';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { toast } from 'sonner';
import { FaPlus, FaEdit, FaTrash, FaToggleOn, FaToggleOff, FaTimes } from 'react-icons/fa';

export default function AdminBanners() {
  const { t, i18n } = useTranslation();
  const { API, user } = useContext(AppContext);
  const [banners, setBanners] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingBanner, setEditingBanner] = useState(null);
  const [formData, setFormData] = useState({
    title: '',
    message: '',
    type: 'promotional',
    link_url: '',
    link_text: '',
    background_color: '#635BFF',
    text_color: '#FFFFFF',
    image_url: '',
    image_position: 'left',
    is_active: true,
    display_order: 0
  });
  const [uploadingImage, setUploadingImage] = useState(false);

  useEffect(() => {
    if (user?.role === 'admin') {
      fetchBanners();
    }
  }, [user]);

  const fetchBanners = async () => {
    try {
      const response = await axios.get(`${API}/banners/admin/all?admin_id=${user.id}`);
      setBanners(response.data);
      setLoading(false);
    } catch (error) {
      console.error('Error fetching banners:', error);
      setLoading(false);
    }
  };

  const handleOpenModal = (banner = null) => {
    if (banner) {
      setEditingBanner(banner);
      setFormData({
        title: banner.title,
        message: banner.message,
        type: banner.type,
        link_url: banner.link_url || '',
        link_text: banner.link_text || '',
        background_color: banner.background_color,
        text_color: banner.text_color,
        image_url: banner.image_url || '',
        image_position: banner.image_position || 'left',
        is_active: banner.is_active,
        display_order: banner.display_order
      });
    } else {
      setEditingBanner(null);
      setFormData({
        title: '',
        message: '',
        type: 'promotional',
        link_url: '',
        link_text: '',
        background_color: '#635BFF',
        text_color: '#FFFFFF',
        image_url: '',
        image_position: 'left',
        is_active: true,
        display_order: banners.length
      });
    }
    setShowModal(true);
  };

  const handleImageUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    // Check file size (max 5MB)
    if (file.size > 5 * 1024 * 1024) {
      toast.error(i18n.language === 'fr' ? 'Image trop grande (max 5MB)' : 'Image too large (max 5MB)');
      return;
    }

    setUploadingImage(true);

    try {
      const formDataUpload = new FormData();
      formDataUpload.append('file', file);

      const response = await axios.post(
        `${API}/banners/admin/upload-image?admin_id=${user.id}`,
        formDataUpload,
        {
          headers: {
            'Content-Type': 'multipart/form-data'
          }
        }
      );

      setFormData({...formData, image_url: response.data.image_url});
      toast.success(i18n.language === 'fr' ? 'Image uploadée' : 'Image uploaded');
    } catch (error) {
      console.error('Error uploading image:', error);
      toast.error(i18n.language === 'fr' ? 'Erreur upload' : 'Upload error');
    } finally {
      setUploadingImage(false);
    }
  };

  const handleCloseModal = () => {
    setShowModal(false);
    setEditingBanner(null);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    try {
      if (editingBanner) {
        await axios.put(
          `${API}/banners/admin/update/${editingBanner.id}?admin_id=${user.id}`,
          formData
        );
        toast.success(i18n.language === 'fr' ? 'Bannière mise à jour' : 'Banner updated');
      } else {
        await axios.post(
          `${API}/banners/admin/create?admin_id=${user.id}`,
          formData
        );
        toast.success(i18n.language === 'fr' ? 'Bannière créée' : 'Banner created');
      }
      
      handleCloseModal();
      fetchBanners();
    } catch (error) {
      console.error('Error saving banner:', error);
      toast.error(i18n.language === 'fr' ? 'Erreur' : 'Error');
    }
  };

  const handleDelete = async (bannerId) => {
    if (!confirm(i18n.language === 'fr' ? 'Supprimer cette bannière ?' : 'Delete this banner?')) {
      return;
    }

    try {
      await axios.delete(`${API}/banners/admin/delete/${bannerId}?admin_id=${user.id}`);
      toast.success(i18n.language === 'fr' ? 'Bannière supprimée' : 'Banner deleted');
      fetchBanners();
    } catch (error) {
      console.error('Error deleting banner:', error);
      toast.error(i18n.language === 'fr' ? 'Erreur' : 'Error');
    }
  };

  const handleToggleStatus = async (bannerId) => {
    try {
      await axios.put(`${API}/banners/admin/toggle/${bannerId}?admin_id=${user.id}`);
      fetchBanners();
    } catch (error) {
      console.error('Error toggling banner:', error);
      toast.error(i18n.language === 'fr' ? 'Erreur' : 'Error');
    }
  };

  if (loading) {
    return (
      <AdminLayout>
        <div className="flex items-center justify-center min-h-screen">
          <p>{t('loading')}</p>
        </div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout>
      <div className="p-8">
        <div className="flex justify-between items-center mb-8">
          <div>
            <h1 className="text-3xl font-bold text-[#0A2540]">
              {i18n.language === 'fr' ? 'Gestion des Bannières' : 'Banner Management'}
            </h1>
            <p className="text-gray-600 mt-2">
              {i18n.language === 'fr' 
                ? 'Gérez les bannières publicitaires du site' 
                : 'Manage site banner ads'}
            </p>
          </div>
          <button
            onClick={() => handleOpenModal()}
            className="bg-[#635BFF] text-white px-6 py-3 rounded-lg hover:bg-[#5248d9] flex items-center gap-2 font-semibold"
          >
            <FaPlus />
            {i18n.language === 'fr' ? 'Nouvelle Bannière' : 'New Banner'}
          </button>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
          <Card>
            <CardContent className="p-6">
              <p className="text-sm text-gray-600">{i18n.language === 'fr' ? 'Total' : 'Total'}</p>
              <p className="text-3xl font-bold text-[#0A2540]">{banners.length}</p>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-6">
              <p className="text-sm text-gray-600">{i18n.language === 'fr' ? 'Actives' : 'Active'}</p>
              <p className="text-3xl font-bold text-[#635BFF]">
                {banners.filter(b => b.is_active).length}
              </p>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-6">
              <p className="text-sm text-gray-600">{i18n.language === 'fr' ? 'Inactives' : 'Inactive'}</p>
              <p className="text-3xl font-bold text-gray-600">
                {banners.filter(b => !b.is_active).length}
              </p>
            </CardContent>
          </Card>
        </div>

        {/* Banners List */}
        <div className="grid gap-6">
          {banners.length === 0 ? (
            <Card>
              <CardContent className="p-12 text-center">
                <p className="text-gray-600 mb-4">
                  {i18n.language === 'fr' ? 'Aucune bannière' : 'No banners'}
                </p>
                <button
                  onClick={() => handleOpenModal()}
                  className="bg-[#635BFF] text-white px-6 py-2 rounded-lg hover:bg-[#5248d9]"
                >
                  {i18n.language === 'fr' ? 'Créer la première' : 'Create first one'}
                </button>
              </CardContent>
            </Card>
          ) : (
            banners.map((banner) => (
              <Card key={banner.id} className={!banner.is_active ? 'opacity-60' : ''}>
                <CardContent className="p-6">
                  {/* Preview */}
                  <div
                    className="mb-4 p-4 rounded-lg"
                    style={{
                      backgroundColor: banner.background_color,
                      color: banner.text_color
                    }}
                  >
                    <p className="font-semibold text-center">{banner.message}</p>
                  </div>

                  {/* Info */}
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-4">
                    <div>
                      <p className="text-xs text-gray-600">{i18n.language === 'fr' ? 'Type' : 'Type'}</p>
                      <p className="font-semibold capitalize">{banner.type}</p>
                    </div>
                    <div>
                      <p className="text-xs text-gray-600">{i18n.language === 'fr' ? 'Ordre' : 'Order'}</p>
                      <p className="font-semibold">{banner.display_order}</p>
                    </div>
                    <div>
                      <p className="text-xs text-gray-600">{i18n.language === 'fr' ? 'Lien' : 'Link'}</p>
                      <p className="font-semibold text-sm truncate">{banner.link_url || 'N/A'}</p>
                    </div>
                    <div>
                      <p className="text-xs text-gray-600">{i18n.language === 'fr' ? 'Statut' : 'Status'}</p>
                      <span className={`px-2 py-1 rounded text-xs font-semibold ${
                        banner.is_active ? 'bg-[#635BFF]/10 text-[#5248d9]' : 'bg-gray-100 text-gray-700'
                      }`}>
                        {banner.is_active ? (i18n.language === 'fr' ? 'Active' : 'Active') : (i18n.language === 'fr' ? 'Inactive' : 'Inactive')}
                      </span>
                    </div>
                  </div>

                  {/* Actions */}
                  <div className="flex gap-3">
                    <button
                      onClick={() => handleToggleStatus(banner.id)}
                      className={`flex items-center gap-2 px-4 py-2 rounded-lg font-semibold ${
                        banner.is_active
                          ? 'bg-gray-200 text-gray-700 hover:bg-gray-300'
                          : 'bg-[#635BFF] text-white hover:bg-[#635BFF]'
                      }`}
                    >
                      {banner.is_active ? <FaToggleOff /> : <FaToggleOn />}
                      {banner.is_active 
                        ? (i18n.language === 'fr' ? 'Désactiver' : 'Deactivate')
                        : (i18n.language === 'fr' ? 'Activer' : 'Activate')}
                    </button>
                    <button
                      onClick={() => handleOpenModal(banner)}
                      className="bg-[#0A2540] text-white px-4 py-2 rounded-lg hover:bg-[#003a52] flex items-center gap-2"
                    >
                      <FaEdit />
                      {i18n.language === 'fr' ? 'Modifier' : 'Edit'}
                    </button>
                    <button
                      onClick={() => handleDelete(banner.id)}
                      className="bg-red-500 text-white px-4 py-2 rounded-lg hover:bg-red-600 flex items-center gap-2"
                    >
                      <FaTrash />
                      {i18n.language === 'fr' ? 'Supprimer' : 'Delete'}
                    </button>
                  </div>
                </CardContent>
              </Card>
            ))
          )}
        </div>

        {/* Modal */}
        {showModal && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-lg max-w-2xl w-full max-h-[90vh] overflow-y-auto">
              <div className="p-6">
                <div className="flex justify-between items-center mb-6">
                  <h2 className="text-2xl font-bold text-[#0A2540]">
                    {editingBanner 
                      ? (i18n.language === 'fr' ? 'Modifier Bannière' : 'Edit Banner')
                      : (i18n.language === 'fr' ? 'Nouvelle Bannière' : 'New Banner')}
                  </h2>
                  <button onClick={handleCloseModal} className="text-gray-500 hover:text-gray-700">
                    <FaTimes size={24} />
                  </button>
                </div>

                <form onSubmit={handleSubmit}>
                  <div className="space-y-4">
                    {/* Message */}
                    <div>
                      <label className="block text-sm font-semibold mb-2">
                        {i18n.language === 'fr' ? 'Message *' : 'Message *'}
                      </label>
                      <textarea
                        required
                        value={formData.message}
                        onChange={(e) => setFormData({...formData, message: e.target.value})}
                        className="w-full border border-gray-300 rounded-lg px-4 py-2"
                        rows="3"
                      />
                    </div>

                    {/* Type */}
                    <div>
                      <label className="block text-sm font-semibold mb-2">
                        {i18n.language === 'fr' ? 'Type' : 'Type'}
                      </label>
                      <select
                        value={formData.type}
                        onChange={(e) => setFormData({...formData, type: e.target.value})}
                        className="w-full border border-gray-300 rounded-lg px-4 py-2"
                      >
                        <option value="promotional">Promotionnel</option>
                        <option value="commercial">Commercial</option>
                        <option value="informational">Informatif</option>
                      </select>
                    </div>

                    {/* Link */}
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <label className="block text-sm font-semibold mb-2">
                          {i18n.language === 'fr' ? 'Lien URL' : 'Link URL'}
                        </label>
                        <input
                          type="text"
                          value={formData.link_url}
                          onChange={(e) => setFormData({...formData, link_url: e.target.value})}
                          className="w-full border border-gray-300 rounded-lg px-4 py-2"
                          placeholder="/page"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-semibold mb-2">
                          {i18n.language === 'fr' ? 'Texte du bouton' : 'Button Text'}
                        </label>
                        <input
                          type="text"
                          value={formData.link_text}
                          onChange={(e) => setFormData({...formData, link_text: e.target.value})}
                          className="w-full border border-gray-300 rounded-lg px-4 py-2"
                          placeholder="En savoir plus"
                        />
                      </div>
                    </div>

                    {/* Image Upload */}
                    <div>
                      <label className="block text-sm font-semibold mb-2">
                        {i18n.language === 'fr' ? 'Image (optionnelle)' : 'Image (optional)'}
                      </label>
                      <div className="space-y-3">
                        {/* Image URL or Upload */}
                        <div className="flex gap-2">
                          <input
                            type="text"
                            value={formData.image_url}
                            onChange={(e) => setFormData({...formData, image_url: e.target.value})}
                            className="flex-1 border border-gray-300 rounded-lg px-4 py-2"
                            placeholder={i18n.language === 'fr' ? 'URL de l\'image ou...' : 'Image URL or...'}
                          />
                          <label className="bg-blue-500 text-white px-4 py-2 rounded-lg hover:bg-blue-600 cursor-pointer flex items-center gap-2">
                            <input
                              type="file"
                              accept="image/*"
                              onChange={handleImageUpload}
                              className="hidden"
                              disabled={uploadingImage}
                            />
                            {uploadingImage ? '⏳' : '📁'} {i18n.language === 'fr' ? 'Upload' : 'Upload'}
                          </label>
                        </div>

                        {/* Image Position */}
                        <div>
                          <label className="block text-xs text-gray-600 mb-1">
                            {i18n.language === 'fr' ? 'Position de l\'image' : 'Image position'}
                          </label>
                          <select
                            value={formData.image_position}
                            onChange={(e) => setFormData({...formData, image_position: e.target.value})}
                            className="w-full border border-gray-300 rounded-lg px-4 py-2"
                          >
                            <option value="left">{i18n.language === 'fr' ? 'Gauche' : 'Left'}</option>
                            <option value="right">{i18n.language === 'fr' ? 'Droite' : 'Right'}</option>
                            <option value="background">{i18n.language === 'fr' ? 'Fond (arrière-plan)' : 'Background'}</option>
                          </select>
                        </div>

                        {/* Image Preview */}
                        {formData.image_url && (
                          <div className="relative">
                            <img
                              src={formData.image_url.startsWith('http') ? formData.image_url : `${API}${formData.image_url}`}
                              alt="Preview"
                              className="max-h-20 rounded-lg border border-gray-300"
                              onError={(e) => {e.target.style.display = 'none'}}
                            />
                            <button
                              type="button"
                              onClick={() => setFormData({...formData, image_url: ''})}
                              className="absolute -top-2 -right-2 bg-red-500 text-white rounded-full w-6 h-6 flex items-center justify-center hover:bg-red-600"
                            >
                              ×
                            </button>
                          </div>
                        )}
                      </div>
                    </div>

                    {/* Colors */}
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <label className="block text-sm font-semibold mb-2">
                          {i18n.language === 'fr' ? 'Couleur fond' : 'Background Color'}
                        </label>
                        <input
                          type="color"
                          value={formData.background_color}
                          onChange={(e) => setFormData({...formData, background_color: e.target.value})}
                          className="w-full h-12 border border-gray-300 rounded-lg"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-semibold mb-2">
                          {i18n.language === 'fr' ? 'Couleur texte' : 'Text Color'}
                        </label>
                        <input
                          type="color"
                          value={formData.text_color}
                          onChange={(e) => setFormData({...formData, text_color: e.target.value})}
                          className="w-full h-12 border border-gray-300 rounded-lg"
                        />
                      </div>
                    </div>

                    {/* Order & Active */}
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <label className="block text-sm font-semibold mb-2">
                          {i18n.language === 'fr' ? 'Ordre affichage' : 'Display Order'}
                        </label>
                        <input
                          type="number"
                          min="0"
                          value={formData.display_order}
                          onChange={(e) => setFormData({...formData, display_order: parseInt(e.target.value)})}
                          className="w-full border border-gray-300 rounded-lg px-4 py-2"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-semibold mb-2">
                          {i18n.language === 'fr' ? 'Statut' : 'Status'}
                        </label>
                        <label className="flex items-center gap-3 mt-2">
                          <input
                            type="checkbox"
                            checked={formData.is_active}
                            onChange={(e) => setFormData({...formData, is_active: e.target.checked})}
                            className="w-5 h-5"
                          />
                          <span>{i18n.language === 'fr' ? 'Active' : 'Active'}</span>
                        </label>
                      </div>
                    </div>

                    {/* Preview */}
                    <div>
                      <label className="block text-sm font-semibold mb-2">
                        {i18n.language === 'fr' ? 'Aperçu' : 'Preview'}
                      </label>
                      <div
                        className="p-4 rounded-lg text-center"
                        style={{
                          backgroundColor: formData.background_color,
                          color: formData.text_color
                        }}
                      >
                        <p className="font-semibold">{formData.message || '(Message vide)'}</p>
                      </div>
                    </div>
                  </div>

                  {/* Actions */}
                  <div className="flex gap-3 mt-6">
                    <button
                      type="submit"
                      className="flex-1 bg-[#635BFF] text-white px-6 py-3 rounded-lg hover:bg-[#5248d9] font-semibold"
                    >
                      {editingBanner 
                        ? (i18n.language === 'fr' ? 'Mettre à jour' : 'Update')
                        : (i18n.language === 'fr' ? 'Créer' : 'Create')}
                    </button>
                    <button
                      type="button"
                      onClick={handleCloseModal}
                      className="px-6 py-3 border border-gray-300 rounded-lg hover:bg-gray-100"
                    >
                      {i18n.language === 'fr' ? 'Annuler' : 'Cancel'}
                    </button>
                  </div>
                </form>
              </div>
            </div>
          </div>
        )}
      </div>
    </AdminLayout>
  );
}
